/**
 * API client for per-order buyer/contact notifications.
 *
 * Backend: app/routes/portal_order_notifications.py (mounted under /api/portal).
 */
import api from './api';
import type {
  NotificationContact,
  OrderContactDirectoryResponse,
  OrderNotificationsResponse,
  PortalContact,
  PortalContactCreateRequest,
  PortalContactUpdateRequest,
  SendNotificationResponse,
  SendNowKind,
} from '../types/orderNotifications';

export const orderNotificationService = {
  /** Merged buyer list for a customer; `orderId` adds the PO-sender suggestion. */
  listCustomerContacts: async (
    customerNo: string,
    orderId?: string | null,
  ): Promise<OrderContactDirectoryResponse> => {
    const response = await api.get<OrderContactDirectoryResponse>(
      `/portal/customers/${encodeURIComponent(customerNo)}/order-contacts`,
      { params: orderId ? { order_id: orderId } : undefined },
    );
    return response.data;
  },

  createCustomerContact: async (
    customerNo: string,
    body: PortalContactCreateRequest,
  ): Promise<PortalContact> => {
    const response = await api.post<PortalContact>(
      `/portal/customers/${encodeURIComponent(customerNo)}/order-contacts`,
      body,
    );
    return response.data;
  },

  updateCustomerContact: async (
    contactId: string,
    body: PortalContactUpdateRequest,
  ): Promise<PortalContact> => {
    const response = await api.patch<PortalContact>(
      `/portal/order-contacts/${contactId}`,
      body,
    );
    return response.data;
  },

  getOrderNotifications: async (
    orderId: string,
  ): Promise<OrderNotificationsResponse> => {
    const response = await api.get<OrderNotificationsResponse>(
      `/portal/orders/${orderId}/notifications`,
    );
    return response.data;
  },

  /** Replace the order's recipient list (empty array clears it). */
  setOrderContacts: async (
    orderId: string,
    contacts: NotificationContact[],
  ): Promise<OrderNotificationsResponse> => {
    const response = await api.put<OrderNotificationsResponse>(
      `/portal/orders/${orderId}/notification-contacts`,
      { contacts },
    );
    return response.data;
  },

  sendNow: async (
    orderId: string,
    kind: SendNowKind,
  ): Promise<SendNotificationResponse> => {
    const response = await api.post<SendNotificationResponse>(
      `/portal/orders/${orderId}/notifications/${kind}/send`,
    );
    return response.data;
  },

  /** Mail the Confirmation Update (current surcharge / freight) to everyone holding the stale one. */
  sendConfirmationUpdate: async (orderId: string): Promise<SendNotificationResponse> => {
    const response = await api.post<SendNotificationResponse>(
      `/portal/orders/${orderId}/notifications/confirmation-update/send`,
    );
    return response.data;
  },
};

export default orderNotificationService;
